import React, { useEffect, useState } from "react";
import { Breadcrumb } from "../../components/ui/Breadcrumb";
import reportIcon from "../../assets/icons/report-icon.svg";
import { useSelector, useDispatch } from "react-redux";
import ReportViews from "./ReportViews";
import { useFlags } from "launchdarkly-react-client-sdk";
import OrganizationSearchBaseComponent from "../../components/OrganizationSearchBaseComponent";
import GTM from "../../GTM";
import { buildGtmOrgContext, getPageReferrerTitle } from "../../utils/helpers";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

// Extract header rendering logic
const renderOrgHeader = (
    organizations,
    userSelectedOrganizations,
    id,
    userSelectedOrganizationReports,
    reportId,
    totalParentOrganizations,
    displayReportView
) => {
    return (
        <div className="d-flex header" style={{ alignItems: "center", gap: 8 }}>
            <img className="icon" src={reportIcon} alt="" />
            <h1 className={"section-heading mb-0p"}>Reports</h1>

            {renderSingleOrgHeader(
                organizations,
                userSelectedOrganizations,
                id
            )}
            {renderMultipleRelatedOrgsHeader(
                userSelectedOrganizations,
                id,
                totalParentOrganizations
            )}
            {renderReportRelatedOrgsHeader(
                userSelectedOrganizationReports,
                reportId,
                totalParentOrganizations,
                displayReportView
            )}
        </div>
    );
};

// Single organization header
const renderSingleOrgHeader = (
    organizations,
    userSelectedOrganizations,
    id
) => {
    if (organizations.length > 1) {
        return null;
    }

    return (
        <>
            <div className="vr"></div>
            <label className="sub-header">
                {userSelectedOrganizations[id]?.related[0]
                    ?.ORGANIZATIONGROUPNAME
                    ? userSelectedOrganizations[id]?.related[0]
                          ?.ORGANIZATIONGROUPNAME
                    : userSelectedOrganizations[id]?.organization
                          ?.ORGNIZATIONNAME}
            </label>
        </>
    );
};

// Multiple related organizations header
const renderMultipleRelatedOrgsHeader = (
    userSelectedOrganizations,
    id,
    totalParentOrganizations
) => {
    if (!(
        userSelectedOrganizations[id]?.related.length > 1 &&
        totalParentOrganizations === 1
    )) {
        return null;
    }

    return (
        <>
            <div className="vr"></div>
            <label className="sub-header">
                {userSelectedOrganizations[id]?.related[0].ORGANIZATIONGROUPNAME
                    ? userSelectedOrganizations[id]?.related[0]
                          .ORGANIZATIONGROUPNAME
                    : userSelectedOrganizations[id]?.organization
                          ?.ORGNIZATIONNAME}
            </label>
        </>
    );
};

// Report related organizations header
const renderReportRelatedOrgsHeader = (
    userSelectedOrganizationReports,
    reportId,
    totalParentOrganizations,
    displayReportView
) => {
    if (!(
        userSelectedOrganizationReports[reportId]?.related?.length > 0 &&
        totalParentOrganizations > 1 &&
        displayReportView
    )) {
        return null;
    }

    return (
        <>
            <div className="vr text-color" style={{ width: 2 }}></div>
            <h1
                className="sub-heading-disabled"
                style={{
                    paddingBottom: 0,
                    fontWeight: 600,
                    fontSize: "1rem",
                    marginTop: "10px",
                }}
            >
                {
                    userSelectedOrganizationReports[reportId]?.related[0]
                        .ORGANIZATIONGROUPNAME
                }
            </h1>
        </>
    );
};

// Main component
function ViewSectionPage(props) {
    const { orgid } = useParams();
    const { displayReports } = useFlags();
    const { user } = useSelector((state) => state.auth);
    const { config } = useSelector((state) => state.app);
    const dispatch = useDispatch();
    const {
        parentOrganizations: organizations,
        organizations: allOrganizations,
        policies,
    } = useSelector((state) => state.userDetails);
    const {
        selectedOrganizations: userSelectedOrganizations,
        selectedOrganizationsId: id,
        totalParentOrganizations,
        organizationsCount,
    } = useSelector((state) => state.organizations);

    const {
        selectedOrganizationsReports: userSelectedOrganizationReports,
        selectedOrganizationsIdReports: reportId,
        selectedRelatedOrganizationsForReports: selectedRelatedOrgForReports,
    } = useSelector((state) => state.reportOrganization);

    const [organizationsSelected, setOrganizationsSelected] = useState([]);

    let previousUrls = props.previousUrl;
    let page_referrer = previousUrls[previousUrls.length - 1];

    const gtm = new GTM(null);

    const page_referrer_url =
        page_referrer === "/login"
            ? `${config.REACT_APP_ISERVICES}:81/LanAuthentication`
            : `${config.REACT_APP_BASE_URL}${page_referrer}`;

    const page_referrer_title = getPageReferrerTitle(
        page_referrer_url,
        user,
        dispatch
    );

    useEffect(() => {
        const userEmail = user.userEmail || "";
        const userId = user ? user.userId : "";
        const userRole = user.isUserInternal ? "internal" : "external";

        const { employerName, relatedOrgNames, organizationGroupId } =
            buildGtmOrgContext(userSelectedOrganizations, id);

        gtm.PageView(
            "run reports ",
            employerName.toLowerCase(),
            userId,
            userRole,
            window.location.href,
            `${config.REACT_APP_BASE_URL}${page_referrer}`,
            page_referrer_title,
            orgid,
            userEmail,
            relatedOrgNames,
            organizationGroupId,
            displayReports
        );
    }, [20000]);

    const [selectedRelatedOrganizations, setSelectedRelatedOrganizations] =
        useState([]);
    const { organizationPreference = [] } = useSelector(
        (state) => state.preferenceSlice
    );

    // OrganizationSearchBaseComponent reports a single org as an object (clearing sends []),
    // but displayReportView gates on .length - normalize so one org still counts as selected.
    const organizationSelected = (value) => {
        if (Array.isArray(value)) {
            setOrganizationsSelected(value);
            return;
        }

        setOrganizationsSelected(value ? [value] : []);
    };

    const handleSelectedRelatedOrganizations = (selectedOrgs) => {
        setSelectedRelatedOrganizations(selectedOrgs);
    };

    const displayReportView =
        totalParentOrganizations === 1 &&
        userSelectedOrganizationReports[reportId]?.related?.length === 0
            ? true
            : userSelectedOrganizationReports[reportId]?.related?.length > 0
              ? selectedRelatedOrgForReports?.length > 0
              : organizationsSelected?.length > 0;

    const handleClick = () => {
        if (user?.isUserInternal) {
            window.open(config.REACT_APP_CLI_INTERNAL, "_blank");
        } else {
            window.open(config.REACT_APP_CLI, "_blank");
        }
    };

    // Should show organization search component
    const shouldShowOrgSearch =
        organizations.length > 1 ||
        userSelectedOrganizations[id]?.related.length > 0;
    return (
        <>
            <Breadcrumb
                links={[
                    {
                        path: "/reportingcapabilities",
                        name: "Reports",
                    },
                ]}
            />
            {displayReports ? (
                <>
                    <div className="section-card view-selection-page">
                        {renderOrgHeader(
                            organizations,
                            userSelectedOrganizations,
                            id,
                            userSelectedOrganizationReports,
                            reportId,
                            totalParentOrganizations,
                            displayReportView
                        )}

                        {shouldShowOrgSearch && (
                            <div style={{ marginTop: 20 }}>
                                <OrganizationSearchBaseComponent
                                    organizations={organizations}
                                    allOrganizations={allOrganizations}
                                    policies={policies}
                                    totalParentOrganizations={
                                        totalParentOrganizations
                                    }
                                    organizationPreference={
                                        organizationPreference
                                    }
                                    userSelectedOrganizations={
                                        userSelectedOrganizations
                                    }
                                    organizationsCount={organizationsCount}
                                    organizationSelected={organizationSelected}
                                    onSelectedRelatedOrganizationsChange={
                                        handleSelectedRelatedOrganizations
                                    }
                                    clearButtonText="Clear selection"
                                />
                            </div>
                        )}
                    </div>
                    <div>
                        <ReportViews
                            displayReportView={displayReportView}
                            organizations={organizations}
                            user={user}
                        />
                    </div>
                </>
            ) : (
                <div className="reportpage">
                    <div>
                        A new<b> reports</b> feature is coming soon... In the
                        meantime <br />
                        please continue to access <b>reports</b> through{" "}
                        <span
                            className="cursor-pointer"
                            onClick={handleClick}
                            onKeyDown={handleClick}
                        >
                            Claim & Leave InSight (CLI).
                        </span>
                    </div>
                </div>
            )}
        </>
    );
}

ViewSectionPage.propTypes = {
    previousUrl: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default ViewSectionPage;
